import React from 'react';
import { C } from '../constants';
import CATALOGO from '../data/aceptaciones-tecnico.json';
import {
  estadoHabilitacion, aceptarCondiciones, revocarCondicion, guardarDocumentoTecnico,
  iniciarSelfieTecnico, verificarTituloTecnico, designarProfesionalEmpresa, aceptarSerProfesional,
} from '../services/habilitacion';

/**
 * TU VERIFICACIÓN PARA OPERAR — la tarjeta del técnico junto a «¿A dónde te giramos?».
 *
 * Decisión del dueño (7-oct-2026): el técnico hace el recorrido del médico de MediNetz. Seis
 * aceptaciones, cada una por separado; su documento; la selfie con el documento y el título, que
 * verifica root. Desde el 1-nov-2026 sin eso no recibe asignaciones, visitas ni giros. Antes del
 * corte opera normal y esta tarjeta le dice qué le falta y cuánto tiempo queda.
 *
 * 🔴 Tres estados, no dos: «no se pudo consultar» NO es «te falta todo». Si el servidor no
 * contesta, se dice eso, y no se pintan las casillas como si estuvieran sin aceptar.
 *
 * Todos los subcomponentes viven a nivel de módulo: uno declarado dentro de otro se remonta en cada
 * render y el campo pierde el foco en cada tecla (`componentes-anidados.test.js`).
 */

const CORTE_MS = Date.parse(CATALOGO.corte);

const CAJA = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 18,
};
const BOTON = {
  background: C.teal, color: C.heading, border: 'none', borderRadius: 8, padding: '7px 14px',
  fontWeight: 800, fontSize: 12, cursor: 'pointer',
};
const CAMPO = {
  background: 'transparent', color: 'inherit', border: `1px solid ${C.border}`, borderRadius: 6,
  padding: '7px 9px', fontSize: 12,
};

/** El texto del error que manda el hub-api, no el genérico. */
function errorDe(e) {
  return (e && e.data && (e.data.detalle || e.data.error)) || (e && e.message) || 'No se pudo completar.';
}

function cuentaRegresiva(ms) {
  if (!(ms > 0)) return null;
  const dias = Math.floor(ms / 86400000);
  const horas = Math.floor((ms % 86400000) / 3600000);
  if (dias > 0) return `${dias} día${dias === 1 ? '' : 's'} y ${horas} h`;
  const min = Math.floor((ms % 3600000) / 60000);
  return `${horas} h y ${min} min`;
}

function Pastilla({ ok, children }) {
  const col = ok ? '#00c781' : C.amber;
  return (
    <span style={{ fontSize: 9.5, fontWeight: 700, color: col, background: `${col}18`, border: `1px solid ${col}44`, borderRadius: 5, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

/** Una aceptación: su casilla, su título, su resumen y el texto completo desplegable. */
function Condicion({ item, aceptada, aceptadaEn, ocupado, onCambiar }) {
  return (
    <div style={{ borderTop: `1px solid ${C.border}55`, padding: '9px 0' }}>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: ocupado ? 'wait' : 'pointer' }}>
        <input
          type="checkbox"
          checked={aceptada}
          disabled={ocupado}
          onChange={(e) => onCambiar(item.clave, e.target.checked)}
          style={{ marginTop: 3, width: 16, height: 16, flex: '0 0 auto' }}
        />
        <span style={{ fontSize: 12, lineHeight: 1.45 }}>
          <strong style={{ color: C.heading }}>{item.titulo}.</strong>{' '}
          <span style={{ color: C.muted }}>{item.resumen}</span>
          {aceptada && aceptadaEn && (
            <span style={{ display: 'block', fontSize: 10.5, color: C.muted, marginTop: 2 }}>
              Aceptada el {new Date(aceptadaEn).toLocaleString('es-CO', { timeZone: 'America/Bogota' })} · versión {CATALOGO.version}
            </span>
          )}
        </span>
      </label>
      <details style={{ marginLeft: 26, marginTop: 4 }}>
        <summary style={{ fontSize: 11, color: C.teal, cursor: 'pointer' }}>Leer el texto completo</summary>
        <div style={{ fontSize: 11.5, color: C.text, lineHeight: 1.5, marginTop: 6 }}>
          {item.texto.map((p) => <p key={p.slice(0, 40)} style={{ margin: '0 0 6px' }}>{p}</p>)}
        </div>
      </details>
    </div>
  );
}

/**
 * 🏢 Lo que le falta a una EMPRESA, con las claves del contrato de root
 * (`alebas.empresa.habilitacion.v1`). Todo vive en ALEBAS: nada de esto se sube en Solar Hub.
 */
const FALTA_EMPRESA = {
  inscripcion_juridica: 'Inscripción en ALEBAS como contratista (persona jurídica con NIT), sin suspensión',
  correo: 'El correo inscrito en ALEBAS para la empresa tiene que ser el de esta cuenta',
  camara_comercio: 'Cámara de comercio cargada en ALEBAS, expedida hace 30 días o menos',
  rut: 'RUT de la empresa cargado en ALEBAS',
  representante_legal: 'Cédula del representante legal cargada en ALEBAS',
  habilitacion_empresa: 'La habilitación de la empresa en ALEBAS',
  empresa_sin_nit: 'Tu empresa no quedó registrada con su NIT: escribe a info@solar-hub.co',
  profesional_tecnico: 'Un profesional técnico designado, que lo aceptó desde su cuenta y está habilitado (selfie y título COPNIA o CONTE)',
};

/**
 * 👷 El profesional de la empresa (root #118). Root solo dice cuatro hechos —designado, aceptado,
 * habilitado y por qué vía—: ni el documento ni el correo del profesional vuelven, así que la pantalla
 * no los muestra. `prof` null = root no informa todavía (no es «no designado»).
 */
function ProfesionalEmpresa({ prof, ocupado, onDesignar }) {
  const [tipo, setTipo] = React.useState('CC');
  const [numero, setNumero] = React.useState('');
  const [correo, setCorreo] = React.useState('');
  const listo = numero.trim() && correo.trim();
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {prof === null
        ? <div style={{ fontSize: 11.5, color: C.muted }}>ALEBAS todavía no informa el estado del profesional de tu empresa.</div>
        : (
          <div style={{ fontSize: 12, color: C.text, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>Designado <Pastilla ok={prof.designado}>{prof.designado ? 'Sí' : 'Pendiente'}</Pastilla></span>
            <span>Aceptó <Pastilla ok={prof.aceptado}>{prof.aceptado ? 'Sí' : 'Pendiente'}</Pastilla></span>
            <span>Habilitado <Pastilla ok={prof.habilitado}>{prof.habilitado ? (prof.titulo_via === 'conte_manual' ? 'CONTE revisado' : 'COPNIA') : 'Pendiente'}</Pastilla></span>
          </div>
        )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
          Tipo
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={{ ...CAMPO, display: 'block', marginTop: 4 }}>
            <option value="CC">Cédula de ciudadanía</option>
            <option value="CE">Cédula de extranjería</option>
            <option value="PPT">Permiso por protección temporal</option>
          </select>
        </label>
        <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
          Número
          <input value={numero} onChange={(e) => setNumero(e.target.value)} inputMode="numeric"
            style={{ ...CAMPO, display: 'block', marginTop: 4, width: 150 }} />
        </label>
        <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
          Correo de su cuenta de técnico
          <input value={correo} onChange={(e) => setCorreo(e.target.value)} type="email"
            style={{ ...CAMPO, display: 'block', marginTop: 4, width: 210 }} />
        </label>
        <button type="button" disabled={ocupado || !listo}
          onClick={() => onDesignar({ tipoDocumento: tipo, numeroDocumento: numero, correo })}
          style={{ ...BOTON, opacity: ocupado || !listo ? 0.5 : 1 }}>
          {prof && prof.designado ? 'Cambiar profesional' : 'Designar profesional'}
        </button>
      </div>
      <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.45 }}>
        Tu empresa no tiene RETIE propio: responde por ella un profesional habilitado como técnico (selfie y título
        COPNIA o CONTE). Después de designarlo, esa persona entra con SU cuenta y acepta con el NIT de la empresa.
        Cambiar de profesional o de correo borra la aceptación.
      </div>
    </div>
  );
}

/** El técnico acepta ser el profesional de una empresa. Solo escribe el NIT: su documento y su correo los pone el servidor. */
function AceptarProfesionalForm({ ocupado, onAceptar }) {
  const [nit, setNit] = React.useState('');
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
        NIT de la empresa (sin dígito de verificación)
        <input value={nit} onChange={(e) => setNit(e.target.value)} inputMode="numeric"
          style={{ ...CAMPO, display: 'block', marginTop: 4, width: 170 }} />
      </label>
      <button type="button" disabled={ocupado || !nit.trim()} onClick={() => onAceptar(nit)}
        style={{ ...BOTON, opacity: ocupado || !nit.trim() ? 0.5 : 1 }}>
        Aceptar ser su profesional
      </button>
      <div style={{ flex: '1 1 100%', fontSize: 10.5, color: C.muted, lineHeight: 1.45 }}>
        Solo si una empresa instaladora te designó como su profesional técnico. Al aceptar, tu habilitación
        (selfie y título) respalda los trabajos de esa empresa. Se acepta con el documento y el correo de esta cuenta.
      </div>
    </div>
  );
}

/** El NIT de la empresa: se registra una vez. Es el número con que root la habilita. */
function NitForm({ ocupado, onGuardar }) {
  const [numero, setNumero] = React.useState('');
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 6 }}>
      <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
        NIT de la empresa (sin dígito de verificación)
        <input value={numero} onChange={(e) => setNumero(e.target.value)} inputMode="numeric"
          style={{ ...CAMPO, display: 'block', marginTop: 4, width: 170 }} />
      </label>
      <button type="button" disabled={ocupado || !numero.trim()} onClick={() => onGuardar('NIT', numero)}
        style={{ ...BOTON, opacity: ocupado || !numero.trim() ? 0.5 : 1 }}>
        Registrar NIT
      </button>
      <div style={{ flex: '1 1 100%', fontSize: 10.5, color: C.muted }}>
        Una vez registrado no se cambia desde la cuenta: es el NIT con el que ALEBAS habilita a tu empresa.
      </div>
    </div>
  );
}

/** El documento: se registra una vez. Lo que root verifica es ESTE número. */
function DocumentoForm({ sugerido, ocupado, onGuardar }) {
  const [tipo, setTipo] = React.useState('CC');
  const [numero, setNumero] = React.useState(sugerido || '');
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 6 }}>
      <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
        Tipo
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={{ ...CAMPO, display: 'block', marginTop: 4 }}>
          <option value="CC">Cédula de ciudadanía</option>
          <option value="CE">Cédula de extranjería</option>
          <option value="PPT">Permiso por protección temporal</option>
          <option value="PA">Pasaporte</option>
        </select>
      </label>
      <label style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>
        Número
        <input value={numero} onChange={(e) => setNumero(e.target.value)} inputMode="numeric"
          style={{ ...CAMPO, display: 'block', marginTop: 4, width: 170 }} />
      </label>
      <button type="button" disabled={ocupado || !numero.trim()} onClick={() => onGuardar(tipo, numero)}
        style={{ ...BOTON, opacity: ocupado || !numero.trim() ? 0.5 : 1 }}>
        Registrar documento
      </button>
      <div style={{ flex: '1 1 100%', fontSize: 10.5, color: C.muted }}>
        Una vez registrado no se cambia desde la cuenta: es el número que root verifica contra tu selfie.
        {sugerido && ' Lo precargamos con la cédula de tu medio de cobro; confírmalo.'}
      </div>
    </div>
  );
}

export default function VerificacionTecnico({ email }) {
  const [est, setEst] = React.useState(null);
  const [consulta, setConsulta] = React.useState('pendiente'); // pendiente · ok · fallo
  const [fallo, setFallo] = React.useState('');
  const [ocupado, setOcupado] = React.useState('');
  const [aviso, setAviso] = React.useState('');
  const [reloj, setReloj] = React.useState(Date.now());

  const cargar = React.useCallback(async (fresco = false) => {
    if (!email) return;
    try {
      const r = await estadoHabilitacion(email, { fresco });
      setEst(r);
      setConsulta('ok');
      setFallo('');
    } catch (e) {
      setConsulta('fallo');
      setFallo(errorDe(e));
    }
  }, [email]);

  React.useEffect(() => {
    // Verifik devuelve a `?view=home&token=…`. Al volver se pregunta FRESCO (sin la caché de root)
    // y se quita el `token` de la barra: no es nuestro y no tiene por qué quedar en el historial.
    let fresco = false;
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get('token')) {
        fresco = true;
        u.searchParams.delete('token');
        window.history.replaceState(null, '', `${u.pathname}${u.search}${u.hash}`);
      }
    } catch { /* sin window.location: nada que limpiar */ }
    cargar(fresco);
  }, [cargar]);

  React.useEffect(() => {
    const t = setInterval(() => setReloj(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  if (!email) return null;

  const hacer = async (que, fn) => {
    setOcupado(que);
    setAviso('');
    try {
      const r = await fn();
      if (r && r.ok === false) setAviso(r.error || r.motivo || 'No se pudo completar.');
      await cargar(true);
      return r;
    } catch (e) {
      setAviso(errorDe(e));
      return null;
    } finally {
      setOcupado('');
    }
  };

  const cambiarCondicion = (clave, marcar) => {
    if (!marcar) {
      const ok = typeof window === 'undefined' || window.confirm(
        'Vas a revocar esta aceptación. Desde el 1 de noviembre no podrás operar sin ella. ¿Seguir?',
      );
      if (!ok) return;
      hacer(`revocar:${clave}`, () => revocarCondicion(email, clave));
      return;
    }
    hacer(`aceptar:${clave}`, () => aceptarCondiciones(email, [clave]));
  };

  const iniciarSelfie = async () => {
    const r = await hacer('selfie', () => iniciarSelfieTecnico(email));
    if (r && r.ok && r.enlace) {
      window.location.assign(r.enlace);
    } else if (r && r.ok === false && (r.configurado === false || r.disponible === false)) {
      setAviso(`La verificación con selfie aún no está disponible: ${r.motivo || 'root no la tiene configurada todavía'}. `
        + 'Tu operación sigue normal hasta el 1 de noviembre.');
    }
  };

  const consultarTitulo = async () => {
    const r = await hacer('titulo', () => verificarTituloTecnico(email));
    if (r && r.ok) {
      setAviso(r.apto
        ? 'El COPNIA confirmó tu matrícula vigente.'
        : `El COPNIA no confirmó una matrícula vigente de eléctrica${r.motivo ? ` (${r.motivo})` : ''}. Si tienes tarjeta CONTE, ALEBAS la revisa a mano.`);
    } else if (r && r.disponible === false) {
      setAviso(`La consulta del título aún no está disponible: ${r.motivo}.`);
    }
  };

  const designar = async (datos) => {
    const r = await hacer('profesional', () => designarProfesionalEmpresa(email, datos));
    if (r && r.ok) setAviso(r.nota || 'Profesional designado. Ahora esa persona tiene que aceptar desde su cuenta.');
  };

  const aceptarProfesional = async (nit) => {
    const r = await hacer('aceptar-profesional', () => aceptarSerProfesional(email, nit));
    if (r && r.ok) setAviso('Aceptaste ser el profesional técnico de esa empresa.');
  };

  const antes = Date.now() < CORTE_MS;
  const faltaTiempo = cuentaRegresiva(CORTE_MS - reloj);
  const completo = Boolean(est && est.completo);
  const hab = (est && est.habilitacion) || {};
  const esEmpresa = Boolean(est && est.empresa);
  const requisito = esEmpresa ? 'la habilitación de tu empresa por NIT en ALEBAS' : 'la verificación de identidad y título';
  const porClave = new Map(((est && est.aceptaciones) || []).map((a) => [a.clave, a]));
  const aceptadas = (est && est.aceptaciones) ? est.aceptaciones.filter((a) => a.aceptada).length : null;

  return (
    <div style={{ ...CAJA, borderLeft: `3px solid ${completo ? '#00c781' : C.amber}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.heading }}>Tu verificación para operar</div>
        {consulta === 'ok' && <Pastilla ok={completo}>{completo ? 'Habilitado' : 'Pendiente'}</Pastilla>}
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
        {antes
          ? <>Hasta el <strong style={{ color: C.heading }}>1 de noviembre de 2026</strong> operas normal.{faltaTiempo && <> Quedan <strong style={{ color: C.heading }}>{faltaTiempo}</strong>.</>} Desde ese día, sin las cinco aceptaciones y {requisito} no podrás recibir asignaciones, visitas ni giros. Es la misma verificación para Solar Hub y FluxAI.</>
          : <>Desde el 1 de noviembre de 2026, sin las cinco aceptaciones y {requisito} <strong style={{ color: C.heading }}>no puedes recibir asignaciones, visitas ni giros</strong>. Puedes seguir entrando a tu consola.</>}
      </div>

      {consulta === 'pendiente' && <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>Consultando tu verificación…</div>}
      {consulta === 'fallo' && (
        <div style={{ background: `${C.amber}1A`, border: `1px solid ${C.amber}66`, borderRadius: 8, padding: '9px 12px', marginTop: 10, fontSize: 12, color: '#7A4A00', lineHeight: 1.45 }}>
          <strong>No se pudo consultar tu verificación.</strong> Esto NO significa que te falte todo. {fallo}
          <div><button type="button" onClick={() => cargar(true)} style={{ ...BOTON, marginTop: 6 }}>Reintentar</button></div>
        </div>
      )}

      {consulta === 'ok' && est && (
        <>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.6, margin: '12px 0 2px' }}>
            1 · Las cinco aceptaciones {aceptadas != null && <span style={{ color: C.muted, fontWeight: 600 }}>({aceptadas} de {CATALOGO.aceptaciones.length})</span>}
          </div>
          <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 2 }}>
            Cada una se acepta por separado y queda registrada con fecha, versión e IP. No hay contrato laboral:
            es una licencia de uso y alquiler de herramientas.
          </div>
          {CATALOGO.aceptaciones.map((item) => {
            const a = porClave.get(item.clave);
            return (
              <Condicion
                key={item.clave}
                item={item}
                aceptada={Boolean(a && a.aceptada)}
                aceptadaEn={a ? a.aceptada_en : null}
                ocupado={Boolean(ocupado)}
                onCambiar={cambiarCondicion}
              />
            );
          })}

          <div style={{ fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.6, margin: '12px 0 4px' }}>2 · {esEmpresa ? 'El NIT de tu empresa' : 'Tu documento'}</div>
          {est.documento && est.documento.registrado
            ? <div style={{ fontSize: 12, color: C.text }}>{est.documento.tipo} {est.documento.numero} <Pastilla ok>Registrado</Pastilla></div>
            : esEmpresa
              ? <NitForm ocupado={Boolean(ocupado)}
                  onGuardar={(tipo, numero) => hacer('documento', () => guardarDocumentoTecnico(email, tipo, numero))} />
              : <DocumentoForm sugerido={est.documento_sugerido || ''} ocupado={Boolean(ocupado)}
                  onGuardar={(tipo, numero) => hacer('documento', () => guardarDocumentoTecnico(email, tipo, numero))} />}

          <div style={{ fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.6, margin: '12px 0 4px' }}>3 · {esEmpresa ? 'Habilitación de la empresa (ALEBAS)' : 'Identidad y título (root)'}</div>
          {hab.estado === 'no_disponible' && (
            <div style={{ fontSize: 11.5, color: '#7A4A00', background: `${C.amber}1A`, border: `1px solid ${C.amber}55`, borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
              No se pudo consultar la verificación en root: {hab.motivo}.{' '}
              {antes ? 'Mientras tanto operas normal.' : 'Hasta que responda no se puede confirmar tu habilitación.'}
            </div>
          )}
          {hab.estado === 'sin_documento' && <div style={{ fontSize: 11.5, color: C.muted }}>{esEmpresa ? 'Registra primero el NIT de tu empresa.' : 'Registra primero tu documento.'}</div>}
          {esEmpresa && (hab.estado === 'ok' || hab.estado === 'pendiente') && (
            <div style={{ display: 'grid', gap: 6 }}>
              {hab.habilitado
                ? (
                  <div style={{ fontSize: 12, color: C.text }}>
                    Tu empresa está habilitada en ALEBAS <Pastilla ok>Habilitada</Pastilla>
                    {hab.camara_vence_en && <span style={{ fontSize: 10.5, color: C.muted }}> · la cámara de comercio vence el {hab.camara_vence_en}</span>}
                  </div>
                )
                : (hab.faltan || []).map((f) => (
                  <div key={f} style={{ fontSize: 12, color: C.text }}>{FALTA_EMPRESA[f] || f} <Pastilla>Pendiente</Pastilla></div>
                ))}
            </div>
          )}
          {esEmpresa && (hab.estado === 'ok' || hab.estado === 'pendiente') && (
            <>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.6, margin: '12px 0 4px' }}>4 · El profesional técnico de tu empresa</div>
              <ProfesionalEmpresa prof={hab.profesional || null} ocupado={Boolean(ocupado)} onDesignar={designar} />
            </>
          )}
          {esEmpresa && (
            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8, lineHeight: 1.45 }}>
              Tu empresa no hace selfie ni consulta COPNIA: la habilita ALEBAS con su expediente de contratista
              (cámara de comercio, RUT y cédula del representante legal), inscrito con este mismo correo, y con su
              profesional técnico. Los documentos
              se cargan en ALEBAS, no aquí. Para inscribirla o corregir algo escribe a{' '}
              <a href="mailto:info@solar-hub.co" style={{ color: C.teal }}>info@solar-hub.co</a>.
            </div>
          )}
          {!esEmpresa && (hab.estado === 'ok' || hab.estado === 'pendiente') && (
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: C.text }}>
                  Selfie con tu documento (Verifik) {hab.identidad_ok ? <Pastilla ok>Verificada</Pastilla> : <Pastilla>{hab.selfie && hab.selfie.estado ? `En proceso: ${hab.selfie.estado}` : 'Pendiente'}</Pastilla>}
                </span>
                {!hab.identidad_ok && (
                  <button type="button" disabled={Boolean(ocupado)} onClick={iniciarSelfie} style={{ ...BOTON, opacity: ocupado ? 0.5 : 1 }}>
                    {ocupado === 'selfie' ? 'Abriendo…' : 'Hacer la selfie'}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: C.text }}>
                  Título profesional {hab.titulo_ok
                    ? <Pastilla ok>{hab.titulo_via === 'conte_manual' ? 'CONTE revisado por ALEBAS' : 'Verificado'}</Pastilla>
                    : <Pastilla>Pendiente</Pastilla>}
                </span>
                {!hab.titulo_ok && (
                  <button type="button" disabled={Boolean(ocupado)} onClick={consultarTitulo} style={{ ...BOTON, opacity: ocupado ? 0.5 : 1 }}>
                    {ocupado === 'titulo' ? 'Consultando…' : 'Consultar en el COPNIA'}
                  </button>
                )}
              </div>
              {!hab.titulo_ok && est.conte && est.conte.numero && (
                <div style={{ fontSize: 10.5, color: C.muted }}>
                  Tienes tarjeta CONTE ({est.conte.numero}): el COPNIA no la cubre y ALEBAS la revisa a mano.
                </div>
              )}
            </div>
          )}
          {!esEmpresa && est.documento && est.documento.registrado && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11.5, color: C.teal, cursor: 'pointer', fontWeight: 700 }}>¿Una empresa instaladora te designó como su profesional técnico?</summary>
              <div style={{ marginTop: 6 }}>
                <AceptarProfesionalForm ocupado={Boolean(ocupado)} onAceptar={aceptarProfesional} />
              </div>
            </details>
          )}
          {!esEmpresa && (
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8, lineHeight: 1.45 }}>
            La selfie es un dato sensible y no estás obligado a darla: si prefieres la revisión a distancia, escribe a{' '}
            <a href="mailto:info@solar-hub.co" style={{ color: C.teal }}>info@solar-hub.co</a>. Ver{' '}
            <a href="/terminos" style={{ color: C.teal }}>Términos</a> y{' '}
            <a href="/privacidad" style={{ color: C.teal }}>Privacidad</a>.
          </div>
          )}
        </>
      )}

      {aviso && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 8 }}>{aviso}</div>}
    </div>
  );
}
